import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import Handsontable from 'handsontable';
import * as _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentReceivableGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { AssignmentEditColumnMultiSelectWithReset } from './AssignmentEditColumnMultiSelectWithReset';
import { AssignmentEditConfirmButton } from './AssignmentEditConfirmButton';
import { AssignmentEditHandsontable } from './AssignmentEditTable';
import { AssignmentSummaryDrawer } from './AssignmentInfoBox';
import { AssignmentMultiAssignModal } from './AssignmentMultiAssignModal';
import {
  AssignmentLoadingSkeleton,
  AssignmentSkeletonLoadingTables,
} from './AssignmentSkeletonLoadingTables';
import {
  AssignmentHighlightBox,
  AssignmentTemplate,
} from './AssignmentTemplate';
import {
  AssignmentEditTableKey,
  formatNewDataAtPropForSelectedReceivables,
  formatSignaledAssignmentsFromReceivablesHotData,
  getColumnSelectOptions,
  getHiddenColumnsFromLocalStorage,
  getInitialHiddenColumns,
  getNumberOfSelectedReceivables,
  getRequiredAssignmentTableColumnHeaders,
  getSharedAssignableCfIds,
  disabledCheckboxHandsontableRenderer,
  groupAssignmentReceivables,
  parseAssignmentReceivablesFromJson,
  getEligibilityBaseFieldsColumnIndices,
} from './utils';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useSimpleConfirm } from 'packages/common-frontend/hooks/useSimpleConfirm';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditAgreementEglibilityTestsBaseFieldsQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import {
  useCreateReceivableResponseTapePreviewsFromPayloadMutation,
  useGetAssignmentReceivablesQuery,
  useGetInitialConditionAssignmentReceivablesQuery,
  useGetLatestAssignmentReceivablesQuery,
} from 'packages/loanProduct/requests/requests.gql.generated';

// Register handsontable custom renderers //
// !TODO @eldarshamukhamedov Module import side-effect, this will cause problems eventually
Handsontable.renderers.registerRenderer(
  'disabledCheckbox',
  disabledCheckboxHandsontableRenderer,
);

// Constants
const exitPromptMessage = `Are you sure you want to leave?'\n\nMake sure you have saved any changes by clicking "Save Version."`;
const assignmentEditHelpText =
  'Select the dropdown menu that you wish to edit. To preview the changes, click on the "Save Version" button. Once you are ready to confirm the changes, click on the "Confirm Assignment" button.';

// Exports
export const AssignmentEditTablesContainer = ({
  assignmentReceivables,
  masterLoanTapeIds,
  reportingDate,
  fileVersion,
  creditFacilities,
  organizationId,
}: {
  assignmentReceivables: AssignmentReceivableGQLType[];
  masterLoanTapeIds: string[];
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  creditFacilities: {
    id: string;
    borrowerEntityName: string;
  }[];
  organizationId: string;
}) => {
  const toast = useToast();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(null!);

  const proposedRef = useRef(null);
  const unassignedRef = useRef(null);
  const previouslyAssignedRef = useRef(null);

  const [
    currentAssignmentReceivables,
    setCurrentAssignmentReceivables,
  ] = useState(assignmentReceivables);
  const [haveUnsubmittedAssignments, setHaveUnsubmittedAssignments] = useState(
    true,
  );
  const [isPreviewGenerating, setIsPreviewGenerating] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    data: eligibilityBaseFieldsData,
    loading: eligibilityBaseFieldsLoading,
  } = useGetCreditAgreementEglibilityTestsBaseFieldsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: latestAssignmentReceivablesData,
    loading: latestAssignmentReceivablesLoading,
  } = useGetLatestAssignmentReceivablesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      masterLoanTapeIds,
    },
  });

  const {
    data: initialConditionAssignmentReceivablesData,
    loading: initialConditionAssignmentReceivablesLoading,
  } = useGetInitialConditionAssignmentReceivablesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { masterLoanTapeIds },
  });

  const {
    data: assignmentReceivablesData,
    loading: assignmentReceivablesLoading,
    startPolling: startAssignmentReceivablesPolling,
    stopPolling: stopAssignmentReceivablesPolling,
    refetch: refetchAssignmentReceivables,
  } = useGetAssignmentReceivablesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      previewIdentifier: '', // will be filled in with newly created preview identifier when refetching right before polling starts
      reportingDate,
      fileVersion,
    },
    onCompleted: (data) => {
      if (
        data.assignmentReceivablesWithMetadata &&
        data.assignmentReceivablesWithMetadata.assignmentReceivables.length
      ) {
        stopAssignmentReceivablesPolling();
        if (timerRef.current) {
          clearTimeout(timerRef.current); // clear the current polling setTimeout
        }
        timerRef.current = undefined;

        setCurrentAssignmentReceivables(
          parseAssignmentReceivablesFromJson(
            data.assignmentReceivablesWithMetadata.assignmentReceivables,
          ),
        );
        setIsPreviewGenerating(false);

        // open summary drawer when new assignment receivables are returned after preview
        onOpen();
        if (!toast.isActive(successToastConfig.id)) {
          successToastConfig.title =
            'Successfully generated assignment preview';
          toast(successToastConfig);
        }
      }
    },
    notifyOnNetworkStatusChange: true, // ensures onCompleted is performed after every poll
  });

  const [
    createReceivableResponseTapePreviewsFromPayload,
    { loading: createReceivableResponseTapePreviewsFromPayloadLoading },
  ] = useCreateReceivableResponseTapePreviewsFromPayloadMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      refetchAssignmentReceivables({
        previewIdentifier:
          data.createReceivableResponseTapePreviewsFromPayload.response,
        reportingDate,
        fileVersion,
      });

      startAssignmentReceivablesPolling(1000);

      timerRef.current = setTimeout(() => {
        stopAssignmentReceivablesPolling();
        timerRef.current = undefined;

        if (!toast.isActive(errorToastConfig.id)) {
          errorToastConfig.title = `Preview request timed out. Please try again.`;
          toast(errorToastConfig);
        }
        refetchAssignmentReceivables();

        Sentry.addBreadcrumb({
          category: 'ui',
          message: `Assignment preview polling timed out`,
          level: Sentry.Severity.Warning,
        });
        setIsPreviewGenerating(false);
      }, 120000);
    },
    notifyOnNetworkStatusChange: true,
  });

  // Display browser warning popup when user attempts to manually refresh or close page
  useEffect(() => {
    const alertCallback = (event: Event) => {
      // using all 3 methods of beforeunload activation to cover most browser cases
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      return '';
    };

    window.addEventListener('beforeunload', alertCallback);

    // remove exit prompt event listener & any remaining setTimeouts when component unmounts to prevent memory leak
    return () => {
      window.removeEventListener('beforeunload', alertCallback);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const getInitialBaseFieldColumnSelections = useCallback(() => {
    const initialHiddenColumnSettings = getInitialHiddenColumns({
      assignmentReceivable: assignmentReceivables[0],
      tableIsAssigned: false,
    });
    const initialHiddenColumnIndices = initialHiddenColumnSettings.columns;
    // the hot persistentState plugin will not save a configuration into local storage if the table instance was not available at the time of the update (in another table)
    // therefore it is possible that there are no basefield selections saved for one table, but there are in another
    const hiddenColumnsFromLocalStorage =
      getHiddenColumnsFromLocalStorage(AssignmentEditTableKey.proposed) ||
      getHiddenColumnsFromLocalStorage(AssignmentEditTableKey.unassigned) ||
      getHiddenColumnsFromLocalStorage(
        AssignmentEditTableKey.previouslyAssigned,
      );

    if (
      !hiddenColumnsFromLocalStorage ||
      !hiddenColumnsFromLocalStorage.columns
    ) {
      return [];
    }

    const hiddenColumnsFromLocalStorageIndices =
      hiddenColumnsFromLocalStorage.columns;

    const preSelectedColumnIndexStrs = _.map(
      _.difference(
        initialHiddenColumnIndices,
        hiddenColumnsFromLocalStorageIndices,
      ),
      _.toString,
    );

    return preSelectedColumnIndexStrs;
  }, [assignmentReceivables]);

  const initialBaseFieldColumnSelections = useMemo(
    () => getInitialBaseFieldColumnSelections(),
    [getInitialBaseFieldColumnSelections],
  );

  // !TODO @eldarshamukhamedov react-router@6: This needs to be replaced with unstable_usePrompt as
  // soon as we switch to a data router.
  useSimpleConfirm({
    when: haveUnsubmittedAssignments,
    message: exitPromptMessage,
  });

  if (
    !latestAssignmentReceivablesData ||
    !latestAssignmentReceivablesData.latestAssignmentReceivablesWithMetadata ||
    latestAssignmentReceivablesLoading ||
    !initialConditionAssignmentReceivablesData ||
    !initialConditionAssignmentReceivablesData.initialConditionAssignmentReceivablesWithMetadata ||
    initialConditionAssignmentReceivablesLoading ||
    eligibilityBaseFieldsLoading ||
    !eligibilityBaseFieldsData ||
    !eligibilityBaseFieldsData.creditAgreementEligibilityTestsBaseFields
  ) {
    return (
      <AssignmentLoadingSkeleton
        dataTestId="loading-assignment-edit-tables"
        editMode
      />
    );
  }

  const parsedInitialConditionAssignmentReceivables = parseAssignmentReceivablesFromJson(
    initialConditionAssignmentReceivablesData
      .initialConditionAssignmentReceivablesWithMetadata.assignmentReceivables,
  );
  const parsedLatestAssignmentReceivables = parseAssignmentReceivablesFromJson(
    latestAssignmentReceivablesData.latestAssignmentReceivablesWithMetadata
      .assignmentReceivables,
  );

  const {
    toAssignReceivables,
    unassignedReceivables,
    previouslyAssignedReceivables,
  } = groupAssignmentReceivables(currentAssignmentReceivables);

  const creditFacilitiesForBorrowerMap = _.keyBy(
    creditFacilities,
    (creditFacility) => creditFacility.id,
  );
  const borrowerEntityNameForCreditFacilityMap = _.keyBy(
    creditFacilities,
    (creditFacility) => creditFacility.borrowerEntityName,
  );

  const getAllReceivablesHotDataFromEditTables = () => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedData = proposedHotCurrent?.hotInstance.getData();
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedData = unassignedHotCurrent?.hotInstance.getData();

    const allReceivablesHotData = _.compact(
      _.concat(proposedData, unassignedData),
    );

    return allReceivablesHotData;
  };

  /**
   * Unhides the selected column indices in all tables in the container
   * @param columnIndexStringsToShow column indices to unhide, as strings
   */
  const showColumnsInAllTables = (columnIndexStringsToShow: string[]) => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedHotInstance = proposedHotCurrent?.hotInstance;
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedHotInstance = unassignedHotCurrent?.hotInstance;
    const previouslyAssignedHotCurrent = previouslyAssignedRef.current as any;
    const previouslyAssignedHotInstance =
      previouslyAssignedHotCurrent?.hotInstance;

    const requiredColumnsLength = getRequiredAssignmentTableColumnHeaders({
      isAssigned: false,
    }).length;

    const columnIndicesToShow = _.sortBy(
      _.map(columnIndexStringsToShow, (columnIndexStr) =>
        _.toNumber(columnIndexStr),
      ),
    );
    const columnIndicesToShowLength = columnIndicesToShow.length;

    const allBaseFieldColumnIndices = _.range(
      requiredColumnsLength,
      assignmentReceivables[0].baseFields.length + requiredColumnsLength + 1,
    );

    const columnIndicesToHide = _.difference(
      allBaseFieldColumnIndices,
      columnIndicesToShow,
    );

    const hiddenColumnsPluginName = 'hiddenColumns';
    const persistentStatePluginName = 'persistentState';

    // for each table, show and hide columns as user selected, rerender table, and snap viewport to the selected basefield columns
    if (proposedHotInstance) {
      const showHideColumnPluginProposed = proposedHotInstance.getPlugin(
        hiddenColumnsPluginName,
      );
      showHideColumnPluginProposed.showColumns(columnIndicesToShow);
      showHideColumnPluginProposed.hideColumns(columnIndicesToHide);
      proposedHotInstance.render();
      proposedHotInstance.scrollViewportTo(
        0,
        columnIndicesToShow[columnIndicesToShowLength - 1],
        false,
        true,
      );

      const hiddenColumns = showHideColumnPluginProposed.getHiddenColumns();
      const psPluginProposed = proposedHotInstance.getPlugin(
        persistentStatePluginName,
      );
      psPluginProposed.saveValue(hiddenColumnsPluginName, {
        columns: hiddenColumns,
        indicators: false,
      });
    }

    if (unassignedHotInstance) {
      const showHideColumnPluginUnassigned = unassignedHotInstance.getPlugin(
        hiddenColumnsPluginName,
      );
      showHideColumnPluginUnassigned.showColumns(columnIndicesToShow);
      showHideColumnPluginUnassigned.hideColumns(columnIndicesToHide);
      unassignedHotInstance.render();
      unassignedHotInstance.scrollViewportTo(
        0,
        columnIndicesToShow[columnIndicesToShowLength - 1],
        false,
        true,
      );

      const hiddenColumns = showHideColumnPluginUnassigned.getHiddenColumns();
      const psPluginUnassigned = unassignedHotInstance.getPlugin(
        persistentStatePluginName,
      );
      psPluginUnassigned.saveValue(hiddenColumnsPluginName, {
        columns: hiddenColumns,
        indicators: false,
      });
    }

    if (previouslyAssignedHotInstance) {
      const showHideColumnPluginPreviouslyAssigned = previouslyAssignedHotInstance.getPlugin(
        hiddenColumnsPluginName,
      );
      showHideColumnPluginPreviouslyAssigned.showColumns(columnIndicesToShow);
      showHideColumnPluginPreviouslyAssigned.hideColumns(columnIndicesToHide);
      previouslyAssignedHotInstance.render();
      previouslyAssignedHotInstance.scrollViewportTo(
        0,
        columnIndicesToShow[columnIndicesToShowLength - 1],
        false,
        true,
      );

      const hiddenColumns = showHideColumnPluginPreviouslyAssigned.getHiddenColumns();
      const psPluginPreviouslyAssigned = previouslyAssignedHotInstance.getPlugin(
        persistentStatePluginName,
      );
      psPluginPreviouslyAssigned.saveValue(hiddenColumnsPluginName, {
        columns: hiddenColumns,
        indicators: false,
      });
    }
  };

  /**
   * Gets the lowest common denominator of all assignable credit facility ids across the selected (via checkbox) receivables
   * @returns an array of shared assignable credit facility id strings
   */
  const getSharedAssignableCfIdsInAllTables = () => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedData = proposedHotCurrent?.hotInstance.getData();
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedData = unassignedHotCurrent?.hotInstance.getData();

    const proposedSelectedColIndex = proposedHotCurrent?.hotInstance.propToCol(
      'selected',
    );
    const unassignedSelectedColIndex = unassignedHotCurrent?.hotInstance.propToCol(
      'selected',
    );
    const proposedAssignableCfIdsColIndex = proposedHotCurrent?.hotInstance.propToCol(
      'assignableCreditFacilityIds',
    );
    const unassignedAssignableCfIdsColIndex = unassignedHotCurrent?.hotInstance.propToCol(
      'assignableCreditFacilityIds',
    );

    const sharedAssignableCfIds = getSharedAssignableCfIds([
      {
        data: proposedData,
        selectedColIndex: proposedSelectedColIndex,
        assignableCfIdsColIndex: proposedAssignableCfIdsColIndex,
      },
      {
        data: unassignedData,
        selectedColIndex: unassignedSelectedColIndex,
        assignableCfIdsColIndex: unassignedAssignableCfIdsColIndex,
      },
    ]);

    return sharedAssignableCfIds;
  };

  /**
   * Gets the number of selected receivables across all tables in the container that are selected by the checkbox
   * @returns number of selected receivables as a number
   */
  const getNumberOfSelectedReceivablesInAllEditTables = () => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedAssignableData = proposedHotCurrent?.hotInstance.getData();
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedAssignableData = unassignedHotCurrent?.hotInstance.getData();

    const proposedSelectedColIndex = proposedHotCurrent?.hotInstance.propToCol(
      'selected',
    );
    const unassignedSelectedColIndex = unassignedHotCurrent?.hotInstance.propToCol(
      'selected',
    );

    const numberOfSelectedReceivables = getNumberOfSelectedReceivables([
      {
        data: proposedAssignableData,
        selectedColIndex: proposedSelectedColIndex,
      },
      {
        data: unassignedAssignableData,
        selectedColIndex: unassignedSelectedColIndex,
      },
    ]);

    return numberOfSelectedReceivables;
  };

  /**
   * Batch updates the selected receivables (via checkbox) across all editable tables in the container to a new proposed owner
   * @param newBorrowerEntityName
   */
  const updateProposedOwnerForReceivables = (
    newBorrowerEntityName: string | undefined,
  ) => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedAssignableData = proposedHotCurrent?.hotInstance.getData();
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedAssignableData = unassignedHotCurrent?.hotInstance.getData();

    const proposedSelectedColIndex = proposedHotCurrent?.hotInstance.propToCol(
      'selected',
    );
    const unassignedSelectedColIndex = unassignedHotCurrent?.hotInstance.propToCol(
      'selected',
    );

    const newProposedDataAtProp = formatNewDataAtPropForSelectedReceivables({
      data: proposedAssignableData,
      selectedColIndex: proposedSelectedColIndex,
      prop: 'owner',
      newValue: newBorrowerEntityName,
    });
    const newUnassignedDataAtProp = formatNewDataAtPropForSelectedReceivables({
      data: unassignedAssignableData,
      selectedColIndex: unassignedSelectedColIndex,
      prop: 'owner',
      newValue: newBorrowerEntityName,
    });

    proposedHotCurrent?.hotInstance.setDataAtRowProp(newProposedDataAtProp);
    unassignedHotCurrent?.hotInstance.setDataAtRowProp(newUnassignedDataAtProp);
  };

  // this method currently only needs to get the column ordering in one table in this parent container
  // since all tables in the same container have synchronized column order
  const getIdAndOwnerColumnIndices = () => {
    const hotCurrent = (proposedRef.current ||
      unassignedRef.current ||
      previouslyAssignedRef.current) as any;

    const finleyReceivableIdColIndex = hotCurrent?.hotInstance?.propToCol(
      'finleyReceivableId',
    );
    const ownerColIndex = hotCurrent?.hotInstance?.propToCol('owner');
    return {
      id: finleyReceivableIdColIndex,
      owner: ownerColIndex,
    };
  };

  const columnChangeHistory: {
    [key in AssignmentEditTableKey]: boolean;
  }[] = [];

  /**
   * Manages propogating user column moves so that column order of table instances in the same container are synchronized
   * @param tableKey the tableKey of the table instance that is triggering the move
   * @param movedColumns the columns that were moved
   * @param dropIndex the index that the moved columns were inserted at
   */
  const moveColumnInAllTables = (
    tableKey: AssignmentEditTableKey,
    movedColumns: number[],
    dropIndex?: number,
  ) => {
    const proposedHotCurrent = proposedRef.current as any;
    const unassignedHotCurrent = unassignedRef.current as any;
    const previouslyAssignedHotCurrent = previouslyAssignedRef.current as any;
    const proposedHotInstance = proposedHotCurrent?.hotInstance;
    const unassignedHotInstance = unassignedHotCurrent?.hotInstance;
    const previouslyAssignedHotInstance =
      previouslyAssignedHotCurrent?.hotInstance;

    const tableKeyToHotInstanceUpdateMap: {
      [key in AssignmentEditTableKey]: Partial<
        { [secondaryKey in AssignmentEditTableKey]: any }
      >;
    } = {
      proposed: {
        unassigned: unassignedHotInstance,
        previouslyAssigned: previouslyAssignedHotInstance,
      },
      unassigned: {
        proposed: proposedHotInstance,
        previouslyAssigned: previouslyAssignedHotInstance,
      },
      previouslyAssigned: {
        proposed: proposedHotInstance,
        unassigned: unassignedHotInstance,
      },
    };

    const lastMove = columnChangeHistory[columnChangeHistory.length - 1];

    // if there have not been any recorded column moves, or the previously-recorded column move has finished propogating to all tables, this indicates that we are receiving a brand new user-initiated column move
    // if these conditions are not met, this means that we are receiving a programatically-propogated incoming move, which we should not do anything with, otherwise we will be in an infinite loop of column moves triggering between the tables
    if (
      !lastMove ||
      (!!lastMove.proposed &&
        !!lastMove.unassigned &&
        !!lastMove.previouslyAssigned)
    ) {
      const columnChangeConfig: {
        [key in AssignmentEditTableKey]: boolean;
      } = {
        proposed: false,
        unassigned: false,
        previouslyAssigned: false,
      };

      // push to the columnChangeHistory a new record where the current table's config (column move initiator) is set to true
      columnChangeConfig[tableKey] = true;
      columnChangeHistory.push(columnChangeConfig);

      // update the column order in the other tables, then set the table's config to true in the latest columnChangeHistory entry to make sure the column update does not keep propogating infinitely
      _.forEach(
        tableKeyToHotInstanceUpdateMap[tableKey],
        (hotInstanceToUpdate, key) => {
          // key loses its type through forEach, but is guaranteed in the tableKeyToHotInstanceUpdateMap type
          const castedKey = key as AssignmentEditTableKey;
          const manualColumnMovePlugin = hotInstanceToUpdate?.getPlugin(
            'manualColumnMove',
          );
          manualColumnMovePlugin?.dragColumns(movedColumns, dropIndex);
          columnChangeHistory[columnChangeHistory.length - 1][castedKey] = true;

          // trigger persistentState to store the propogated tables' new column indices
          // needs to be done because persistentState is not saved when changes are triggered with programmatic hooks instead of user
          const updatedColumnIndices = hotInstanceToUpdate?.columnIndexMapper.getIndexesSequence();
          const persistentStatePlugin = hotInstanceToUpdate?.getPlugin(
            'persistentState',
          );
          persistentStatePlugin?.saveValue(
            'manualColumnMove',
            updatedColumnIndices,
          );
        },
      );
    }

    proposedHotInstance?.render();
    unassignedHotInstance?.render();
    previouslyAssignedHotInstance?.render();
  };

  const resizeColumnInAllTables = (
    tableKey: AssignmentEditTableKey,
    newSize: number,
    column: number,
  ) => {
    const proposedHotCurrent = proposedRef.current as any;
    const unassignedHotCurrent = unassignedRef.current as any;
    const previouslyAssignedHotCurrent = previouslyAssignedRef.current as any;
    const proposedHotInstance = proposedHotCurrent?.hotInstance;
    const unassignedHotInstance = unassignedHotCurrent?.hotInstance;
    const previouslyAssignedHotInstance =
      previouslyAssignedHotCurrent?.hotInstance;

    const tableKeyToHotInstanceUpdateMap: {
      [key in AssignmentEditTableKey]: Partial<
        { [secondaryKey in AssignmentEditTableKey]: any }
      >;
    } = {
      proposed: {
        unassigned: unassignedHotInstance,
        previouslyAssigned: previouslyAssignedHotInstance,
      },
      unassigned: {
        proposed: proposedHotInstance,
        previouslyAssigned: previouslyAssignedHotInstance,
      },
      previouslyAssigned: {
        proposed: proposedHotInstance,
        unassigned: unassignedHotInstance,
      },
    };

    const lastResize = columnChangeHistory[columnChangeHistory.length - 1];

    if (
      !lastResize ||
      (!!lastResize.proposed &&
        !!lastResize.unassigned &&
        !!lastResize.previouslyAssigned)
    ) {
      const columnChangeConfig: {
        [key in AssignmentEditTableKey]: boolean;
      } = {
        proposed: false,
        unassigned: false,
        previouslyAssigned: false,
      };

      columnChangeConfig[tableKey] = true;
      columnChangeHistory.push(columnChangeConfig);

      _.forEach(
        tableKeyToHotInstanceUpdateMap[tableKey],
        (hotInstanceToUpdate, key) => {
          const castedKey = key as AssignmentEditTableKey;
          const manualColumnResizePlugin = hotInstanceToUpdate?.getPlugin(
            'manualColumnResize',
          );
          manualColumnResizePlugin?.setManualSize(column, newSize);
          columnChangeHistory[columnChangeHistory.length - 1][castedKey] = true;

          manualColumnResizePlugin?.saveManualColumnWidths();
        },
      );
    }

    proposedHotInstance?.render();
    unassignedHotInstance?.render();
    previouslyAssignedHotInstance?.render();
  };

  /**
   * Resets the column settings to the default state and wipes column configs in localStorage
   */
  const resetColumnSettings = () => {
    const proposedHotCurrent = proposedRef.current as any;
    const proposedHotInstance = proposedHotCurrent?.hotInstance;
    const unassignedHotCurrent = unassignedRef.current as any;
    const unassignedHotInstance = unassignedHotCurrent?.hotInstance;
    const previouslyAssignedHotCurrent = previouslyAssignedRef.current as any;
    const previouslyAssignedHotInstance =
      previouslyAssignedHotCurrent?.hotInstance;

    const hotInstances = [
      proposedHotInstance,
      unassignedHotInstance,
      previouslyAssignedHotInstance,
    ];

    // reset persistent state for each table instance
    _.forEach(hotInstances, (hotInstance) => {
      hotInstance?.runHooks('persistentStateReset');
      hotInstance?.initIndexMappers();
    });

    // update column config plugins for each table instance
    const pluginNamesToUpdate = [
      'manualColumnMove',
      'columnSorting',
      'manualColumnResize',
    ];
    const allPluginsToUpdate = _.flatMap(hotInstances, (hotInstance) => {
      return _.map(pluginNamesToUpdate, (pluginName) =>
        hotInstance?.getPlugin(pluginName),
      );
    });

    _.forEach(allPluginsToUpdate, (pluginToUpdate) => {
      pluginToUpdate?.updatePlugin();
    });

    // restore hidden columns to initial settings
    proposedHotInstance?.updateSettings({
      hiddenColumns: getInitialHiddenColumns({
        assignmentReceivable: toAssignReceivables[0],
        tableIsAssigned: false,
      }),
    });
    unassignedHotInstance?.updateSettings({
      hiddenColumns: getInitialHiddenColumns({
        assignmentReceivable: unassignedReceivables[0],
        tableIsAssigned: false,
      }),
    });
    previouslyAssignedHotInstance?.updateSettings({
      hiddenColumns: getInitialHiddenColumns({
        assignmentReceivable: previouslyAssignedReceivables[0],
        tableIsAssigned: true,
      }),
    });

    // rerender each table instance
    _.forEach(hotInstances, (hotInstance) => {
      hotInstance?.render();
    });
  };

  const baseFieldsColumnSelectOptions = getColumnSelectOptions(
    assignmentReceivables[0].baseFields,
  );
  const eligibilityBaseFieldsColumnIndices = getEligibilityBaseFieldsColumnIndices(
    {
      assignmentReceivable: assignmentReceivables[0],
      eligibilityBaseFields:
        eligibilityBaseFieldsData.creditAgreementEligibilityTestsBaseFields,
    },
  );

  return (
    <Box height="100%" width="100%">
      <AssignmentTemplate
        editMode
        headerChildren={
          <HStack spacing="32px">
            <Button
              onClick={async () => {
                try {
                  setIsPreviewGenerating(true);

                  const {
                    id: receivableIdColumnIndex,
                    owner: receivableOwnerColumnIndex,
                  } = getIdAndOwnerColumnIndices();

                  const signaledAssignments = formatSignaledAssignmentsFromReceivablesHotData(
                    {
                      receivablesHotData: getAllReceivablesHotDataFromEditTables(),
                      receivableIdColumnIndex,
                      receivableOwnerColumnIndex,
                      borrowerEntityNameForCreditFacilityMap,
                    },
                  );

                  await createReceivableResponseTapePreviewsFromPayload({
                    variables: { masterLoanTapeIds, signaledAssignments },
                  });
                } catch (err: any) {
                  if (!toast.isActive(errorToastId)) {
                    errorToastConfig.title = `Error previewing assignment`;
                    toast(errorToastConfig);
                  }
                  throw err;
                }
              }}
              isLoading={
                createReceivableResponseTapePreviewsFromPayloadLoading ||
                assignmentReceivablesLoading ||
                !_.isNil(timerRef.current)
              }
              variant="outline"
              colorScheme="green"
              borderWidth="medium"
            >
              Save Version
            </Button>
            <AssignmentEditConfirmButton
              getSignaledAssignments={() => {
                const {
                  id: receivableIdColumnIndex,
                  owner: receivableOwnerColumnIndex,
                } = getIdAndOwnerColumnIndices();
                return formatSignaledAssignmentsFromReceivablesHotData({
                  receivablesHotData: getAllReceivablesHotDataFromEditTables(),
                  receivableIdColumnIndex,
                  receivableOwnerColumnIndex,
                  borrowerEntityNameForCreditFacilityMap,
                });
              }}
              currentMasterLoanTapeIds={masterLoanTapeIds}
              reportingDate={reportingDate}
              fileVersion={fileVersion}
              onConfirm={() => {
                setHaveUnsubmittedAssignments(false);
              }}
            />
            <AssignmentSummaryDrawer
              latestAssignmentReceivables={
                assignmentReceivablesData &&
                assignmentReceivablesData.assignmentReceivablesWithMetadata &&
                assignmentReceivablesData.assignmentReceivablesWithMetadata
                  .assignmentReceivables.length
                  ? parseAssignmentReceivablesFromJson(
                      assignmentReceivablesData
                        .assignmentReceivablesWithMetadata
                        .assignmentReceivables,
                    )
                  : parsedLatestAssignmentReceivables
              }
              initialConditionAssignmentReceivables={
                parsedInitialConditionAssignmentReceivables
              }
              latestBorrowingBaseReports={
                assignmentReceivablesData &&
                assignmentReceivablesData.assignmentReceivablesWithMetadata &&
                assignmentReceivablesData.assignmentReceivablesWithMetadata
                  .assignmentReceivables.length
                  ? assignmentReceivablesData.assignmentReceivablesWithMetadata
                      .borrowingBaseReports
                  : latestAssignmentReceivablesData
                      .latestAssignmentReceivablesWithMetadata
                      .borrowingBaseReports
              }
              initialConditionBorrowingBaseReports={
                initialConditionAssignmentReceivablesData
                  .initialConditionAssignmentReceivablesWithMetadata
                  .borrowingBaseReports
              }
              creditFacilityIds={_.map(creditFacilities, ({ id }) => id)}
              preview={
                !!(
                  assignmentReceivablesData &&
                  assignmentReceivablesData.assignmentReceivablesWithMetadata
                )
              }
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              fileVersion={fileVersion}
              reportingDate={reportingDate}
            />
          </HStack>
        }
      >
        <Text pt="32px">{assignmentEditHelpText}</Text>
        <HStack
          mt="32px"
          mb="32px"
          justify="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Text>
              {`Reporting date:
              ${commonDate.fullDateStrToHumanDateString(reportingDate)}`}
            </Text>
            <Text>File version: {fileVersion}</Text>
          </Box>
          <Flex justifyContent="flex-end">
            <AssignmentMultiAssignModal
              getNumberOfSelectedReceivables={
                getNumberOfSelectedReceivablesInAllEditTables
              }
              creditFacilitiesForBorrowerMap={creditFacilitiesForBorrowerMap}
              getSharedAssignableCfidOptions={
                getSharedAssignableCfIdsInAllTables
              }
              onApply={(value: string | undefined) => {
                updateProposedOwnerForReceivables(value);
              }}
            />
            <AssignmentEditColumnMultiSelectWithReset
              showColumnsInAllTables={showColumnsInAllTables}
              options={baseFieldsColumnSelectOptions}
              initialSelections={initialBaseFieldColumnSelections}
              resetColumnSettings={resetColumnSettings}
              eligibilityBaseFieldsColumnIndices={
                eligibilityBaseFieldsColumnIndices
              }
            />
          </Flex>
        </HStack>
        <Box>
          {isPreviewGenerating ? (
            <AssignmentHighlightBox>
              <AssignmentSkeletonLoadingTables />
            </AssignmentHighlightBox>
          ) : (
            <AssignmentHighlightBox>
              <AssignmentEditHandsontable
                tableKey={AssignmentEditTableKey.proposed}
                assignmentReceivables={toAssignReceivables}
                creditFacilitiesForBorrowerMap={creditFacilitiesForBorrowerMap}
                receivablesHotRef={proposedRef}
                moveColumnInAllTables={moveColumnInAllTables}
                resizeColumnInAllTables={resizeColumnInAllTables}
                isAssigned={false}
              />
              <AssignmentEditHandsontable
                tableKey={AssignmentEditTableKey.unassigned}
                assignmentReceivables={unassignedReceivables}
                creditFacilitiesForBorrowerMap={creditFacilitiesForBorrowerMap}
                receivablesHotRef={unassignedRef}
                moveColumnInAllTables={moveColumnInAllTables}
                resizeColumnInAllTables={resizeColumnInAllTables}
                isAssigned={false}
              />
              <AssignmentEditHandsontable
                tableKey={AssignmentEditTableKey.previouslyAssigned}
                assignmentReceivables={previouslyAssignedReceivables}
                creditFacilitiesForBorrowerMap={creditFacilitiesForBorrowerMap}
                receivablesHotRef={previouslyAssignedRef}
                moveColumnInAllTables={moveColumnInAllTables}
                resizeColumnInAllTables={resizeColumnInAllTables}
                isAssigned
              />
            </AssignmentHighlightBox>
          )}
        </Box>
      </AssignmentTemplate>
    </Box>
  );
};
